import { Injectable } from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { State } from '@state/core.state';
import { catchError, exhaustMap, map, mergeMap, of, take, firstValueFrom, from } from 'rxjs';
import { withLatestFrom, timeout, switchMap, tap } from 'rxjs/operators';

import {
	RestService,
	PROJECT_CARRIER_MAP_STATUS,
	TruckSearchTable,
	DATE_MDY,
	IS_NOT_NULL_OR_UNDEFINED,
	CarriersProjectsList,
	IS_NOT_EMPTY,
	Owner,
	TruckSearchResult,
	SEARCH_TRUCK_TABLE,
	SEARCH_CARRIER_ONLY_TABLE,
	DIFFERENCE_IN_DAYS,
	ProjectCarrierMapDTO,
	IS_NULL_OR_UNDEFINED,
	Carrier,
} from 'cargo-chief-commons';

import { UrlService } from '@service/url/url.service';
import { UtilService } from '@service/util/util.service';
import { CsvExportService } from '@service/csv-export/csv-export.service';
import { ProjectHelperService } from '@service/projectHelper/project-helper.service';
import { Cargochief } from '@service/cargochief/cargochief.service';
import { SNACKBAR } from '@domain/constants/snackbar-setting';
import {
	CARGOCHIEF_KEYS,
	CAPACITY_SEARCH_FILTER,
	USER_SETTINGS,
} from '@domain/constants/app-variables';
import { PROJECT_TYPES } from '@domain/constants/project-types';
import { FeedbackInformation } from '@notes/note/note.component';
import { TRUCK_TYPES } from '@domain/constants/truck-types';
import { ProductsService } from '@service/products/products.service';
import { AccountSettingsService } from '@service/accountSettings/account-settings.service';
import { CapacitySearchFilterService } from '@service/capacitySearchFilter/capacity-search-filter.service';

import { ProjectDetailsStore } from '../store/project-details.store';
import { projectDetailsPageStateKey, ProjectDetailsState } from '../state';
import { RECOMMENDED_CARRIERS, PROJECT_CARRIER_MAP } from '../actions';
import { ProjectDetailsEffects } from '../effects/project-details.effects';

import { EngageCarriersDialogComponent } from '../../engage-carriers-dialog/engage-carriers-dialog.component';
import { LoginSetupService } from '@service/login-setup/login-setup.service';
import { ProjectGroupService } from '@service/project-group/project-group.service';
import { ProjectUtilService } from '@service/project-util/project-util.service';

@Injectable({
	providedIn: 'root',
})
export class RecommendedCarriersEffects {
	constructor(
		private actions$: Actions<any>,
		private readonly store: Store<State>,
		public readonly projectDetailsStore: ProjectDetailsStore,
		private readonly restService: RestService,
		private readonly urlService: UrlService,
		public utilService: UtilService,
		private dialog: MatDialog,
		private csvExportService: CsvExportService,
		public projectHelperService: ProjectHelperService,
		public cargochief: Cargochief,
		public readonly projectDetailsEffects: ProjectDetailsEffects,
		public productsService: ProductsService,
		public accountSettings: AccountSettingsService,
		private loginSetupService: LoginSetupService,
		private capacitySearchFilterService: CapacitySearchFilterService,
		private projectGroupService: ProjectGroupService,
		private projectUtilService: ProjectUtilService
	) {}

	searchLanes$ = createEffect(() =>
		this.actions$.pipe(
			ofType(RECOMMENDED_CARRIERS.SEARCH_LANES),
			withLatestFrom(this.store.select(projectDetailsPageStateKey)),
			mergeMap((request: any) => {
				const projectDetailsPageState: ProjectDetailsState = request[1];
				if (this.productsService.ASSIGN_CARRIERS) {
					this.getAssigneeList(request[0].usersList);
				}

				if (request[0].hasLane) {
					return this.searchLanes(
						request[0].searchFormData,
						projectDetailsPageState.projectId,
						projectDetailsPageState,
						null
					);
				} else {
					return this.restService
						.GET(this.urlService.getCarriersByCustomerUrl(request[0].projectDTO.customerDTO.id))
						.pipe(
							map((result) => {
								this.searchLanes(
									request[0].searchFormData,
									projectDetailsPageState.projectId,
									projectDetailsPageState,
									result
								);
								return RECOMMENDED_CARRIERS.SEARCH_LANES_SUCCESS({});
							}),
							catchError((error) => {
								this.utilService.showSnackbar(
									'Unable to load recommended carriers list. Please try again later.',
									SNACKBAR.ERROR,
									SNACKBAR.ACTION
								);
								return RECOMMENDED_CARRIERS.SEARCH_LANES_FAILURE({});
							})
						);
				}
			})
		)
	);

	async searchLanes(searchFormData, projectId, projectDetailsPageState, customerOnlyCarriers) {
		try {
			let laneSearchResponse, carrierIds, truckSearchResult;
			if (customerOnlyCarriers) {
				laneSearchResponse = customerOnlyCarriers;
				truckSearchResult = laneSearchResponse['data'];

				truckSearchResult = customerOnlyCarriers.data.map((data) => {
					let truck = {} as TruckSearchResult;
					let carrier: Carrier = data;
					carrier.carrierHubId = carrier.id;

					truck.carrier = carrier;
					return truck;
				});

				carrierIds = laneSearchResponse['data'].map((search) => search.id);
			} else {
				const capacitySearchPayload = {
					...searchFormData,
					projectId,
				};
				let laneSearchResponse = await this.restService
					.POST(this.urlService.getCapacityUrl(), capacitySearchPayload)
					.pipe(
						catchError((error) => {
							console.error('laneSearchResponse failed: ' + error);
							this.utilService.showSnackbar(
								'Unable to run recommended carriers search.',
								SNACKBAR.ERROR,
								SNACKBAR.ACTION
							);
							return of({});
						})
					)
					.toPromise();

				laneSearchResponse.data.forEach((lane) => {
					lane['isLane'] = true;
				});
				this.store.dispatch(
					RECOMMENDED_CARRIERS.SET.TRUCK_LANE_SEARCH_ID({
						id:
							laneSearchResponse['data'].length > 0
								? laneSearchResponse['data'][0]['truckLaneSearchId']
								: null,
					})
				);
				truckSearchResult = laneSearchResponse['data'];
			}

			let allCapacitySearchResults: TruckSearchTable[] = [];
			if (truckSearchResult.length) {
				allCapacitySearchResults = SEARCH_TRUCK_TABLE(
					truckSearchResult,
					!this.utilService.isNullOrUndefined(searchFormData)
						? searchFormData.destination.state
						: ''
				);
				allCapacitySearchResults.forEach((truck: TruckSearchTable) => {
					if (
						(!truck.isLane || truck.isTruckAndLane) &&
						!this.utilService.isNullOrUndefined(searchFormData.pickupDateStart) &&
						!this.utilService.isNullOrUndefined(searchFormData.pickupDateEnd) &&
						DIFFERENCE_IN_DAYS(searchFormData.pickupDateStart, searchFormData.pickupDateEnd) > 1
					) {
						truck.showAvailableDate = true;
					}
				});
			}

			if (customerOnlyCarriers) {
				allCapacitySearchResults = SEARCH_CARRIER_ONLY_TABLE(truckSearchResult);
			}

			if (projectDetailsPageState.capacitySearchSort) {
				allCapacitySearchResults = this.capacitySearchFilterService.sortByColumn(
					projectDetailsPageState.capacitySearchSort,
					allCapacitySearchResults
				);
			}

			this.store.dispatch(
				RECOMMENDED_CARRIERS.SET.ALL_CAPACITY_SEARCH_RESULT({ list: allCapacitySearchResults })
			);
			if (customerOnlyCarriers) {
				this.projectDetailsEffects.setCapacitySearchMatchesList(allCapacitySearchResults);
			} else {
				this.projectDetailsEffects.setCapacitySearchMatchesList(truckSearchResult);
			}

			if (projectDetailsPageState.carrierHQ) {
				this.filterByHQ(projectDetailsPageState.carrierHQ, searchFormData);
			}

			this.projectDetailsEffects.setCapacitySearchDataForProjectCarrierMap();
			return RECOMMENDED_CARRIERS.SEARCH_LANES_SUCCESS({});
		} catch (exception) {
			console.error('recommended-carrers effect failed: ' + exception);
			return RECOMMENDED_CARRIERS.SEARCH_LANES_FAILURE({});
		}
	}

	getAssigneeList(usersList) {
		let userCarrierMapList: any = this.cargochief.getItem(CARGOCHIEF_KEYS.USER_CARRIER_MAPS);
		if (IS_NOT_EMPTY(userCarrierMapList)) {
			userCarrierMapList = JSON.parse(userCarrierMapList);
		} else {
			userCarrierMapList = [];
		}
		this.buildUserCarrierMap(userCarrierMapList, usersList);
	}
	buildUserCarrierMap(userCarrierMapList, usersList) {
		if (
			!this.utilService.isNullOrUndefined(userCarrierMapList) &&
			userCarrierMapList.length > 0 &&
			!this.utilService.isNullOrUndefined(usersList) &&
			usersList.length > 0
		) {
			let userCarrierMap: any = {};
			userCarrierMapList.forEach((cMap: any) => {
				const assignee: Owner = usersList.filter((user) => user.userId == cMap.assigneeId)[0];
				if (!this.utilService.isNullOrUndefined(assignee)) {
					userCarrierMap[cMap.carrierId] = assignee.name;
				}
			});
			this.store.dispatch(RECOMMENDED_CARRIERS.SET.USER_CARRIER.MAP({ userCarrierMap }));
			this.store.dispatch(RECOMMENDED_CARRIERS.SET.USER_CARRIER.LIST({ list: userCarrierMapList }));
		}
	}

	capacitySearchDownloadCSV$ = createEffect(() =>
		this.actions$.pipe(
			ofType(RECOMMENDED_CARRIERS.CAPACITY_SEARCH.DOWNLOAD_CSV),
			withLatestFrom(this.store.select(projectDetailsPageStateKey)),
			mergeMap((request: any) => {
				const projectDetailsPageState: ProjectDetailsState = request[1];
				const capacitySearchResults = request[0].capacitySearchResults,
					searchFormData = request[0].searchFormData;

				const fileHeaders = [
					'Carrier',
					'DOT',
					'MC',
					'Contact First Name',
					'Contact Last Name',
					'Email',
				];
				const truckSearchResult: TruckSearchTable[] = [...capacitySearchResults];
				let searchResults = [];
				let fileName = `Search Results.csv`;
				if (searchFormData) {
					fileName = `${searchFormData?.origin.city} ${searchFormData?.origin.state} - ${
						searchFormData?.destination.city
					} ${searchFormData?.destination.state} (${searchFormData?.trailerType
						.trim()
						.toUpperCase()
						.substring(0, 1)}) (${DATE_MDY(searchFormData.pickupDateStart)} - ${DATE_MDY(
						searchFormData.pickupDateEnd
					)})`;
				}

				if (truckSearchResult) {
					const url = this.urlService.getCarriersMailReceiverContacts();
					const request = {
						carrierIDs: truckSearchResult.map((searchResult) => searchResult.carrierId),
					};
					return this.restService.POST(url, request).pipe(
						timeout(120000),
						map((response: any) => {
							let result = [];
							truckSearchResult.forEach((searchResult) => {
								const contact = response.data.find(
									(contact) => contact.carrierId === searchResult.carrierId
								);
								let row = [
									`"${searchResult.businessName}"`,
									`"${searchResult.firmMc || ''}"`,
									`"${searchResult.firmDot || ''}"`,
									`"${contact?.firstName || ''}"`,
									`"${contact?.lastName || ''}"`,
									`"${contact?.emailAddress?.toLowerCase() || ''}"`,
								];
								result.push(row);
							});
							this.csvExportService.downloadCsvFile(fileName, fileHeaders, result);
							return RECOMMENDED_CARRIERS.CAPACITY_SEARCH.DOWNLOAD_COMPLETE({});
						}),
						catchError((error) => {
							this.utilService.showSnackbar(
								'Unable to fetch carrier contacts. Please try again.',
								SNACKBAR.ERROR,
								SNACKBAR.ACTION
							);
							return of(RECOMMENDED_CARRIERS.CAPACITY_SEARCH.DOWNLOAD_COMPLETE({}));
						})
					);
				}
				return of(RECOMMENDED_CARRIERS.CAPACITY_SEARCH.DOWNLOAD_COMPLETE({}));
			})
		)
	);

	engageWithCarriers$ = createEffect(() =>
		this.actions$.pipe(
			ofType(RECOMMENDED_CARRIERS.ENGAGE_WITH_CARRIERS.START),
			withLatestFrom(this.store.select(projectDetailsPageStateKey)),
			mergeMap((request: any) => {
				const [actionPayload, projectDetailsPageState]: [any, ProjectDetailsState] = request;
				const selectedCarrierIds = actionPayload.selectedCarrierIds,
					capacitySearchResults = projectDetailsPageState.capacitySearchResults,
					noteType = actionPayload.noteType,
					showRateForm = actionPayload.showRateForm,
					showNotes = actionPayload.showNotes;

				if (selectedCarrierIds.length === 1) {
					const carrierMatch: TruckSearchTable = capacitySearchResults.find(
						(searchResult) => searchResult.carrierId === selectedCarrierIds[0]
					);
					return of(
						RECOMMENDED_CARRIERS.ENGAGE_WITH_CARRIERS.OPEN_DIALOG({
							carrierIds: selectedCarrierIds,
							noteType: noteType,
							projectId: projectDetailsPageState.projectId,
							carrierBusinessName: carrierMatch ? carrierMatch.businessName : null,
							feedbackInformation: carrierMatch
								? this.createFeedbackInformationFromOpenedMatch(carrierMatch)
								: null,
							showRateForm,
							showNotes,
						})
					);
				} else {
					return of(
						RECOMMENDED_CARRIERS.ENGAGE_WITH_CARRIERS.CREATE({
							carrierIDs: selectedCarrierIds,
							desiredPrice: null,
							carrierRespondedAt: null,
							status: PROJECT_CARRIER_MAP_STATUS.IN_PROGRESS,
						})
					);
				}
			})
		)
	);

	engageWithCarrier$ = createEffect(() =>
		this.actions$.pipe(
			ofType(RECOMMENDED_CARRIERS.ENGAGE_WITH_CARRIERS.OPEN_DIALOG),
			exhaustMap((data) => {
				let dialogRef = this.dialog.open(EngageCarriersDialogComponent, {
					width: '550px',
					panelClass: 'cargo_chief_dialog_container',
					data,
				});
				return dialogRef.afterClosed();
			}),
			map((result: any) => {
				if (result) {
					if (IS_NOT_NULL_OR_UNDEFINED(result.hideModal)) {
						this.store.dispatch(
							RECOMMENDED_CARRIERS.ENGAGE_WITH_CARRIERS.UPDATE_USER_SETTING({
								updatedSettingValue: result.hideModal,
							})
						);
					}
					this.store.dispatch(
						RECOMMENDED_CARRIERS.ENGAGE_WITH_CARRIERS.CREATE({
							carrierIDs: result.carrierIDs,
							desiredPrice: result.desiredPrice,
							carrierRespondedAt: result.carrierRespondedAt,
							status: result.status,
						})
					);
				}
				return RECOMMENDED_CARRIERS.ENGAGE_WITH_CARRIERS.CLOSE({});
			})
		)
	);

	updateUserSetting$ = createEffect(() =>
		this.actions$.pipe(
			ofType(RECOMMENDED_CARRIERS.ENGAGE_WITH_CARRIERS.UPDATE_USER_SETTING),
			mergeMap((request) => {
				if (request) {
					const url = this.urlService.updateUserSettingUrl({
						settingName: USER_SETTINGS.HIDE_ENGAGE_CARRIER_DIALOG,
						value: request.updatedSettingValue,
					});
					return this.restService.PUT(url, {}).pipe(
						map((response: any) => {
							this.loginSetupService.updateLoginSetup();
							return RECOMMENDED_CARRIERS.ENGAGE_WITH_CARRIERS.UPDATE_USER_SETTING_SUCCESS({});
						}),
						catchError((error) =>
							of(RECOMMENDED_CARRIERS.ENGAGE_WITH_CARRIERS.UPDATE_USER_SETTING_FAILURE({}))
						)
					);
				}
			})
		)
	);

	addEngagedCarriersToProject$ = createEffect(() =>
		this.actions$.pipe(
			ofType(RECOMMENDED_CARRIERS.ENGAGE_WITH_CARRIERS.CREATE),
			withLatestFrom(this.store.select(projectDetailsPageStateKey)),
			mergeMap(([action, projectDetailsPageState]: [any, ProjectDetailsState]) => {
				const payload = {
					carrierIDs: action.carrierIDs,
					desiredPrice: action.desiredPrice,
					carrierRespondedAt: action.carrierRespondedAt,
					status: action.status,
				};
				return this.restService
					.POST(this.urlService.addProjectCarrierMap(projectDetailsPageState.projectId), payload)
					.pipe(
						switchMap((response: ProjectCarrierMapDTO[]) =>
							from(
								(async () => {
									// Update capacity search results
									const capacitySearchResults =
										projectDetailsPageState.capacitySearchResults.filter(
											(search: TruckSearchTable) => !payload.carrierIDs.includes(search.carrierId)
										);

									// Fetch additional project list data for carriers
									const carrierIds = response.map((pcr) => pcr.carrier.id);
									const carrierProjectListData =
										await this.projectUtilService.getCarrierProjectListData(carrierIds);
									response.forEach((pcr) => {
										pcr.projectsList = carrierProjectListData[pcr.carrier.id];
									});
									// Update engaged carriers list
									const engagedCarriersList = this.setPCMFieldsFromCapacitySearch(
										response,
										projectDetailsPageState.capacitySearchResults
									);

									const allProjectCarrierMapsList = [
										...engagedCarriersList,
										...projectDetailsPageState.allProjectCarrierMapsList,
									];

									// Dispatch actions as part of the effect stream
									return [
										RECOMMENDED_CARRIERS.SET.CAPACITY_SEARCH_RESULT({
											list: capacitySearchResults,
										}),
										RECOMMENDED_CARRIERS.SET.NUMBER_OF_LANES({
											count: capacitySearchResults.length,
										}),
										PROJECT_CARRIER_MAP.SET.ALL_PROJECT_CARRIER_MAPS_LIST({
											list: allProjectCarrierMapsList,
										}),
										RECOMMENDED_CARRIERS.ENGAGE_WITH_CARRIERS.SUCCESS({}),
									];
								})()
							).pipe(
								mergeMap((actions) => actions), // Flatten array of actions into the stream
								tap(() => {
									this.utilService.showSnackbar(
										`Carriers added to engaged.`,
										SNACKBAR.SUCCESS,
										SNACKBAR.ACTION
									);
									this.projectDetailsEffects.setEngagedCarriers(
										projectDetailsPageState.allProjectCarrierMapsList
									);
									this.projectUtilService.updateAccountCarrierLanes(
										response,
										projectDetailsPageState
									);
									this.projectDetailsEffects.setCapacitySearchDataForProjectCarrierMap();
								})
							)
						),
						catchError((error) => {
							this.utilService.showSnackbar(
								'Unable to add carriers to project now.',
								SNACKBAR.ERROR,
								SNACKBAR.ACTION
							);
							return of(
								RECOMMENDED_CARRIERS.ENGAGE_WITH_CARRIERS.FAILURE({
									payload: { message: error.error },
								})
							);
						})
					);
			})
		)
	);

	createFeedbackInformationFromOpenedMatch(table: TruckSearchTable): FeedbackInformation {
		return {
			originCity: table.originCity,
			originState: table.originState,
			destCity: table.destCity,
			destState: table.destState,
			trailerTypes: table.trailerTypes,
			truckId: table.id,
			truckSearchId: !table.isLane ? table.searchId : null,
			truckLaneSearchId: table.isLane ? table.searchId : null,
			truckType: table.isLane ? TRUCK_TYPES.LANE : TRUCK_TYPES.TRUCK,
		};
	}

	recommendedToProcure$ = createEffect(() =>
		this.actions$.pipe(
			ofType(RECOMMENDED_CARRIERS.PROCURE_CARRIERS.CREATE),
			withLatestFrom(this.store.select(projectDetailsPageStateKey)),
			mergeMap((request: any) => {
				const projectDetailsPageState: ProjectDetailsState = request[1];
				const selectedCarrierIds = request[0].selectedCarrierIds;
				if (selectedCarrierIds.length === 1) {
					return this.recommendCarrierProcureWithRate(selectedCarrierIds, projectDetailsPageState);
				}
				return this.moveCarriersFromRecommendedToProcured(
					selectedCarrierIds,
					projectDetailsPageState
				);
			})
		)
	);

	async moveCarriersFromRecommendedToProcured(
		selectedCarrierIds: string[],
		projectDetailsPageState: ProjectDetailsState
	) {
		const { projectGroupPayload, noGroupSelected } =
			await this.selectProjectGroup(projectDetailsPageState);
		if (noGroupSelected) {
			this.utilService.showSnackbar(
				'Cannot add carrier to project without selecting a group',
				SNACKBAR.ERROR,
				SNACKBAR.ACTION
			);
			return;
		}
		const payload = {
			carrierIDs: selectedCarrierIds,
			desiredPrice: null,
			carrierRespondedAt: null,
			status: PROJECT_CARRIER_MAP_STATUS.PROCURED,
			...projectGroupPayload,
		};
		let procuredCarriersResponse = await this.restService
			.POST(this.urlService.addProjectCarrierMap(projectDetailsPageState.projectId), payload)
			.toPromise();
		return this.afterMovingCarriersFromRecommendedToProcure(
			selectedCarrierIds,
			procuredCarriersResponse,
			projectDetailsPageState
		);
	}

	async recommendCarrierProcureWithRate(
		selectedCarrierIds,
		projectDetailsPageState: ProjectDetailsState
	) {
		const { projectGroupPayload, noGroupSelected } =
			await this.selectProjectGroup(projectDetailsPageState);
		if (noGroupSelected) {
			this.utilService.showSnackbar(
				'Cannot add carrier to project without selecting a group',
				SNACKBAR.ERROR,
				SNACKBAR.ACTION
			);
			return;
		}
		let desiredPrice = null,
			carrierRespondedAt = null;
		let result = await this.projectHelperService.openCarrierRateDialog({
			hasRate: false,
			title: 'Do you have a rate for this?',
		});
		desiredPrice = result && result.desiredPrice;
		carrierRespondedAt = result && result.carrierRespondedAt;
		const payload = {
			carrierIDs: selectedCarrierIds,
			desiredPrice,
			carrierRespondedAt,
			status: PROJECT_CARRIER_MAP_STATUS.PROCURED,
			...projectGroupPayload,
		};

		let procuredCarriersResponse = await this.restService
			.POST(this.urlService.addProjectCarrierMap(projectDetailsPageState.projectId), payload)
			.toPromise();
		return this.afterMovingCarriersFromRecommendedToProcure(
			selectedCarrierIds,
			procuredCarriersResponse,
			projectDetailsPageState
		);
	}

	async afterMovingCarriersFromRecommendedToProcure(
		selectedCarrierIds,
		procuredCarriersResponse: ProjectCarrierMapDTO[],
		projectDetailsPageState: ProjectDetailsState
	) {
		const carrierIds = procuredCarriersResponse.map((pcr) => pcr.carrier.id);
		const carrierProjectListData =
			await this.projectUtilService.getCarrierProjectListData(carrierIds);
		procuredCarriersResponse.forEach((pcr) => {
			pcr.projectsList = carrierProjectListData[pcr.carrier.id];
		});
		this.utilService.showSnackbar(`Carriers moved to procured.`, SNACKBAR.SUCCESS, SNACKBAR.ACTION);
		let procuredCarriersList = this.setPCMFieldsFromCapacitySearch(
			procuredCarriersResponse,
			projectDetailsPageState.capacitySearchResults
		);
		const allProjectCarrierMapsList = procuredCarriersList.concat(
			projectDetailsPageState.allProjectCarrierMapsList
		);
		this.projectDetailsEffects.setProcuredCarriers(allProjectCarrierMapsList);
		this.store.dispatch(
			PROJECT_CARRIER_MAP.SET.ALL_PROJECT_CARRIER_MAPS_LIST({
				list: allProjectCarrierMapsList,
			})
		);
		const capacitySearchResults = [].concat(
			projectDetailsPageState.capacitySearchResults.filter(
				(search: TruckSearchTable) => selectedCarrierIds.indexOf(search.carrierId) === -1
			)
		);
		this.store.dispatch(
			RECOMMENDED_CARRIERS.SET.CAPACITY_SEARCH_RESULT({
				list: capacitySearchResults,
			})
		);
		this.projectUtilService.updateAccountCarrierLanes(
			procuredCarriersResponse,
			projectDetailsPageState
		);
		this.projectDetailsEffects.setCapacitySearchDataForProjectCarrierMap();
		return RECOMMENDED_CARRIERS.PROCURE_CARRIERS.SUCCESS({});
	}

	setPCMFieldsFromCapacitySearch(
		projectCarrierMapList: ProjectCarrierMapDTO[],
		capacitySearchResults
	) {
		projectCarrierMapList.forEach((pcm) => {
			const search = capacitySearchResults.find((search) => search.carrierId === pcm.carrier.id);
			if (search) {
				pcm.laneCount = search.lanesCount;
				pcm.notesCount = search.notesCount;
				if (IS_NULL_OR_UNDEFINED(pcm.carrier.customerCarrier)) {
					pcm.carrier.customerCarrier =
						pcm.carrier.accountIdSources?.indexOf(this.cargochief.ACCOUNT_ID) !== -1;
				}
				if (
					IS_NULL_OR_UNDEFINED(pcm.recentFeedbackDate) &&
					IS_NOT_NULL_OR_UNDEFINED(search.recentFeedbackDate)
				) {
					pcm.recentFeedbackDate = search.recentFeedbackDate;
				}
			}
		});
		return projectCarrierMapList;
	}

	async filterByHQ(filterSelected: string, searchTruckForm: any) {
		this.store
			.select((state) => state[projectDetailsPageStateKey])
			.pipe(take(1))
			.subscribe(async (state) => {
				const DHO = this.cargochief.getItem(CARGOCHIEF_KEYS.ORIGIN_DEADHEAD_SLIDER),
					DHD = this.cargochief.getItem(CARGOCHIEF_KEYS.DESTINATION_DEADHEAD_SLIDER);
				let latitude, longitude, maxRadius;
				if (filterSelected == CAPACITY_SEARCH_FILTER.CARRIER_HQ[0]) {
					latitude = searchTruckForm.origin.latitude;
					longitude = searchTruckForm.origin.longitude;
					if (!this.utilService.isNullOrUndefined(DHO)) {
						maxRadius = JSON.parse(DHO);
					} else {
						maxRadius = this.accountSettings.SEARCH_TRUCK_ORIGIN_DEADHEAD_LIMIT;
					}
				} else {
					latitude = searchTruckForm.destination.latitude;
					longitude = searchTruckForm.destination.longitude;
					if (!this.utilService.isNullOrUndefined(DHD)) {
						maxRadius = JSON.parse(DHD);
					} else {
						maxRadius = this.accountSettings.SEARCH_TRUCK_DESTINATION_DEADHEAD_LIMIT;
					}
				}
				const capacitySearchResultsCarrierIds: string[] = state.allCapacitySearchResults.map(
					(search) => search.carrierId
				);
				if (!capacitySearchResultsCarrierIds.length) {
					return;
				}
				const payload = {
					carrierIds: capacitySearchResultsCarrierIds.join(','),
					latitude: latitude,
					longitude: longitude,
					maxRadius: maxRadius,
				};

				const url = this.urlService.searchCarrierHQ(0, capacitySearchResultsCarrierIds.length);
				const carrierHQResponse = await this.restService.POST(url, payload).toPromise();
				let capacitySearchResults = this.projectDetailsEffects.getAllUniqueCapacitySearchResults(
					state.allCapacitySearchResults,
					state.allProjectCarrierMapsList,
					state.powerUnits
				);
				const matchedCarrierHqIds = carrierHQResponse.data.map((carrier) => carrier.id);
				capacitySearchResults = capacitySearchResults.filter(
					(search) => matchedCarrierHqIds.indexOf(search.carrierId) !== -1
				);
				this.store.dispatch(
					RECOMMENDED_CARRIERS.SET.MATCHED_CARRIER_HQ_IDS({ carrierIds: matchedCarrierHqIds })
				);
				this.projectDetailsEffects.setCapacitySearchResults(capacitySearchResults);
			});
	}

	removeHQFilter() {
		this.store
			.select((state) => state[projectDetailsPageStateKey])
			.pipe(take(1))
			.subscribe(async (state) => {
				let capacitySearchResults = this.projectDetailsEffects.getAllUniqueCapacitySearchResults(
					state.allCapacitySearchResults,
					state.allProjectCarrierMapsList,
					state.powerUnits
				);
				this.store.dispatch(RECOMMENDED_CARRIERS.SET.MATCHED_CARRIER_HQ_IDS({ carrierIds: [] }));
				this.projectDetailsEffects.setCapacitySearchResults(capacitySearchResults);
			});
	}

	async selectProjectGroup(projectDetailsPageState: ProjectDetailsState): Promise<{
		projectGroupPayload: { manageProjectGroup?: boolean; projectGroupId?: string };
		noGroupSelected: boolean;
	}> {
		const project = projectDetailsPageState.projectDTO;
		const projectTypesToCheck = [PROJECT_TYPES.WATERFALL.value, PROJECT_TYPES.DEDICATED_LANE.value];
		let projectGroupPayload = {};
		let noGroupSelected = false;
		if (projectTypesToCheck.includes(project.type)) {
			let { projectGroupId, projectHasNoGroups } =
				await this.projectGroupService.selectProjectGroup(project.id);
			if (projectHasNoGroups) {
				projectGroupPayload = {
					manageProjectGroup: true,
				};
			} else if (IS_NOT_NULL_OR_UNDEFINED(projectGroupId)) {
				projectGroupPayload = {
					projectGroupId,
				};
			} else {
				noGroupSelected = true;
			}
		}
		return new Promise((resolve) => resolve({ projectGroupPayload, noGroupSelected }));
	}
}
